import React from 'react';
import useBreakpoints from '@hooks/useBreakpoints';
import { AttendeeConnection, AttendeeConnectionStatus } from '@graphql/gql/graphql';
import { NetworkStatus, useQuery } from '@apollo/client';
import { AttendeeConnections } from '@state/index';
import Carousel from '@cvent/carina/components/Carousel';
import PendingCardContainer from '@components/connections/PendingCardContainer';
import { useTranslate } from 'nucleus-text';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { CSSObject } from '@emotion/react';
import useTheme from '@hooks/useTheme';
import { SetConnectionAlert } from './types';

const useStyles = (): Record<string, CSSObject> => {
  const { font, palette } = useTheme();
  const { isL, isXL } = useBreakpoints();

  return {
    carouselContainer: {
      display: 'flex',
      flexDirection: 'column',
      margin: isL || isXL ? '0 -8px' : 0
    },
    header: {
      color: palette?.primary.textOnSeed,
      fontWeight: font.base.weight.bold,
      marginTop: 40,
      marginLeft: 18
    }
  };
};

const useCarouselCols = (isPanelOpen): number => {
  const { isMobile, isL, isXL } = useBreakpoints();

  let cols;
  if (isMobile) {
    cols = 1;
  } else if (isL && isPanelOpen) {
    cols = 2;
  } else if (isL) {
    cols = 3;
  } else if (isXL) {
    cols = 3;
  } else {
    cols = 2;
  }

  return cols;
};

type Props = {
  setConnectionAlert: SetConnectionAlert;
  onSelectAttendee: (attendeeId: string) => void;
  isPanelOpen?: boolean;
};

export const PENDING_CONNECTIONS_PAGE_SIZE = 10;
export const PENDING_CONNECTIONS_SEARCH_CRITERIA = {
  status: AttendeeConnectionStatus.Pending,
  limit: PENDING_CONNECTIONS_PAGE_SIZE
};

/**
 * Renders a carousel of pending connections
 * @constructor
 */
function PendingConnections({ setConnectionAlert, onSelectAttendee, isPanelOpen }: Props): JSX.Element {
  const carouselCols = useCarouselCols(isPanelOpen);
  const { carouselContainer, header } = useStyles();
  const { translate } = useTranslate();

  const variables = {
    criteria: PENDING_CONNECTIONS_SEARCH_CRITERIA
  };

  const { data, error, networkStatus } = useQuery(AttendeeConnections.Queries.SEARCH_ATTENDEE_CONNECTIONS, {
    variables,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });

  const pending: Array<AttendeeConnection> = data?.searchAttendeeConnections?.data || [];

  if (error) {
    return <div {...injectTestId('pending-connections-error')} />;
  }
  if (networkStatus === NetworkStatus.loading) {
    return null;
  }
  if (!pending.length) {
    return <div {...injectTestId('pending-connections-empty')} />;
  }

  return (
    <div css={carouselContainer}>
      <h3 {...injectTestId('pending-connections-list-header')} css={header}>
        {translate('Connections-Page-Pending-List-Header')}
      </h3>
      <Carousel
        testID="pending-connections-list"
        cols={carouselCols}
        hideDots={false}
        scrollLock
        previousAccessibilityLabel={translate('Connections-Page-Pending-Carousel-Left-Arrow-A11Y')}
        nextAccessibilityLabel={translate('Connections-Page-Pending-Carousel-Right-Arrow-A11Y')}
      >
        {pending.map(pend => {
          const { contactId } = pend;

          return (
            <PendingCardContainer
              key={contactId}
              attendee={pend}
              setConnectionAlert={setConnectionAlert}
              onSelectAttendee={onSelectAttendee}
              // Set this only if we have more cards than what is initially displayed - otherwise carousel behaves weirdly
              setActiveFocus={pending.length > carouselCols}
            />
          );
        })}
      </Carousel>
    </div>
  );
}

export default PendingConnections;
